import React from 'react';
import { StyledIconButton } from './iconButton.styles';
import { useTheme } from '@mui/material';
import Icon from '../icon/icon';
import {
  iconButtonPropTypes,
  iconButtonDefaultProps,
} from './iconButton.propTypes';

/**
 * IconButton component renders a button with an icon and customizable styles.
 *
 * @component
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.hasBackground - Determines if the button has a background.
 * @param {string} [props.backgroundColor] - Background color for the button. Can be a valid CSS color string or one of the theme colors: 'primary', 'secondary'.
 * @param {string} props.color - Color for the button. Can be 'default', 'inherit', 'primary', or 'secondary'.
 * @param {string} props.type - Icon type for the button. Should be one of the valid icon types.
 * @param {string} [props.width] - Width of the button.
 * @param {string} [props.height] - Height of the button.
 * @param {string} [props.hoverColor] - Hover color for the button. Should be a valid CSS color string.
 * @param {string} [props.shape] - Shape of the button. Can be 'circle', 'square', or 'roundedSquare'.
 * @param {function} [props.onClick] - Click event handler for the button.
 * @param {string} [props.size] - Size for the icon inside the button. Can be 'small', 'medium', 'large', or 'text'.
 *
 * @returns {JSX.Element} The rendered IconButton component.
 */
export default function IconButton({
  hasBackground,
  backgroundColor,
  color,
  type,
  width,
  height,
  hoverColor,
  shape,
  onClick,
  size,
  ariaLabel,
  ariaExpanded,
  ...rest
}) {
  const theme = useTheme();
  return (
    <StyledIconButton
      hasBackground={hasBackground}
      backgroundColor={backgroundColor}
      color={color}
      width={width}
      height={height}
      hoverColor={hoverColor}
      shape={shape}
      theme={theme}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      {...rest}
    >
      <Icon type={type} fontSize={size} />
    </StyledIconButton>
  );
}

IconButton.propTypes = iconButtonPropTypes;

IconButton.defaultProps = iconButtonDefaultProps;
